import Button from 'components/Button'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentIllustration,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from 'components/ModalSheet'
import React from 'react'
import { observer } from 'mobx-react'
import { Form, SelectContainer, TextAreaContainer } from 'components/inputs'
import InviteTeamMemberModalViewModel from './InviteTeamMemberModalViewModel'
import { Id } from 'types/definitions'
import ContentLoading from 'components/ContentLoading'
import ContentError from 'components/ContentError'
import { useBrandingSettings } from 'hooks/branding'

interface Props {
  vm: InviteTeamMemberModalViewModel
}

function formatButtonText(count: number) {
  return count === 0
    ? 'Send'
    : count === 1
      ? 'Send Invite'
      : `Send ${count} Invites`
}

export default observer(function InviteToTeamModal({ vm }: Props) {
  const { corporateMode } = useBrandingSettings()
  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.sendInvite.pending ? undefined : vm.close}
    >
      <ModalSheetHeader withCloseButton />

      {vm.activate.match({
        pending: () => (
          <ModalSheetContent>
            <ContentLoading />
          </ModalSheetContent>
        ),
        rejected: (err) => (
          <ModalSheetContent>
            <ContentError error={err} />,
          </ModalSheetContent>
        ),
        resolved: () => (
          <>
            <ModalSheetContent>
              <ModalSheetContentBlock>
                {!corporateMode && (
                  <ModalSheetContentIllustration
                    src={require('./illustration.svg')}
                    alt="Illustration of a group"
                  />
                )}
                <ModalSheetContentTitle>
                  Invite people to your team
                </ModalSheetContentTitle>
                <ModalSheetContentDescription>
                  Lets get the ball rolling.
                </ModalSheetContentDescription>
              </ModalSheetContentBlock>
              <ModalSheetContentBlock>
                <Form onSubmit={vm.sendInvite}>
                  <TextAreaContainer
                    label="Email address(es)"
                    help="You can comma- or newline-separate emails to invite multiple people."
                    className="textarea"
                    error={vm.validation.getError('email')}
                    rows={5}
                    model={vm}
                    field="email"
                  />
                  <br />
                  <SelectContainer
                    label="Role"
                    items={vm.teamRoles.map((role) => [
                      Id.assert(role.id),
                      role.name,
                    ])}
                    model={vm}
                    field="teamRoleId"
                    help="When someone accepts the invitation, they are given this role."
                  >
                    {vm.teamRoles.map((role) => (
                      <option
                        key={Id.assert(role.id)}
                        value={Id.assert(role.id)}
                      >
                        {role.name}
                      </option>
                    ))}
                  </SelectContainer>
                </Form>
              </ModalSheetContentBlock>
            </ModalSheetContent>
          </>
        ),
      })}
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button onClick={vm.close} disabled={vm.sendInvite.pending}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={vm.sendInvite}
            loading={vm.sendInvite.pending}
            disabled={vm.sendInvite.pending}
          >
            {formatButtonText(vm.emailAddresses.length)}
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})
